/**
 * SaldoHistoryRow — satu baris riwayat transaksi (order atau ledger) di
 * halaman Riwayat Transaksi (plan 19 Fase 1).
 *
 * Badge:
 *   - Top-up (hijau)            — ledger topup / order paid
 *   - Pemakaian (ungu)          — ledger debit
 *   - Menunggu pembayaran (abu) — order pending
 */

import { StyleSheet, View } from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { Brand, Colors, Radii, Spacing } from '@/constants/theme';
import { formatCreditDelta, formatIdr } from '@/lib/billing-format';
import type { BillingTransactionItem } from '@/lib/api/billing';

type BadgeVisual = { label: string; bg: string; fg: string };

function badgeFor(item: BillingTransactionItem): BadgeVisual {
  if (item.row_type === 'order' && item.status === 'pending') {
    return { label: 'Menunggu pembayaran', bg: '#eceaef', fg: '#6b6573' };
  }
  if (item.kind === 'debit') {
    return { label: 'Pemakaian', bg: 'rgba(86, 63, 122, 0.10)', fg: Brand.primary };
  }
  if (item.kind === 'refund' || item.status === 'refunded') {
    return { label: 'Refund', bg: '#e0f2f1', fg: '#0A7473' };
  }
  if (item.kind === 'adjustment') {
    return { label: 'Penyesuaian', bg: '#eceaef', fg: '#6b6573' };
  }
  // 'credit_topup' order (paid/expired/dll) atau ledger 'topup'.
  return { label: 'Top-up', bg: '#e6f4ea', fg: '#1e7e34' };
}

function titleFor(item: BillingTransactionItem): string {
  if (item.row_type === 'order') {
    return item.status === 'pending' ? 'Order top-up (menunggu)' : 'Top-up point';
  }
  switch (item.kind) {
    case 'topup':
      return 'Point masuk';
    case 'debit':
      return 'Pemakaian point';
    case 'refund':
      return 'Refund point';
    default:
      return 'Penyesuaian saldo';
  }
}

export function SaldoHistoryRow({ item }: { item: BillingTransactionItem }) {
  const badge = badgeFor(item);
  return (
    <View style={styles.row}>
      <View style={styles.copy}>
        <ThemedText style={styles.title}>{titleFor(item)}</ThemedText>
        <ThemedText themeColor="textSecondary" style={styles.meta}>
          {item.ref ?? item.created_at}
        </ThemedText>
      </View>
      <View style={styles.right}>
        <ThemedText style={styles.amount}>
          {item.row_type === 'order' && item.amount_idr !== null
            ? formatIdr(item.amount_idr)
            : formatCreditDelta(item.credit)}
        </ThemedText>
        <View style={[styles.badge, { backgroundColor: badge.bg }]}>
          <ThemedText style={[styles.badgeText, { color: badge.fg }]}>{badge.label}</ThemedText>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderColor: '#e5e5e5',
    borderRadius: Radii.medium,
    borderWidth: 1,
    flexDirection: 'row',
    gap: Spacing.two,
    padding: Spacing.three,
  },
  copy: { flex: 1, minWidth: 0, gap: 2 },
  title: { color: Colors.light.text, fontSize: 15, fontWeight: '700' },
  meta: { fontSize: 12, lineHeight: 18 },
  right: { alignItems: 'flex-end', gap: Spacing.one },
  amount: { color: Colors.light.text, fontSize: 15, fontWeight: '700' },
  badge: { borderRadius: Radii.pill, paddingHorizontal: Spacing.two, paddingVertical: 2 },
  badgeText: { fontSize: 10, fontWeight: '700' },
});
